import { endpoint } from "@services/api.services";
import { getInputField } from "@utils/function/index.function";
import { RedirectData, RedirectFormData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useMemo } from "react";
import { showToast } from "@utils/helper/index.helper";
import { FormField } from "@utils/interface/form.interface";
import { useHandleAction } from "@utils/hook/index.hook";
import { IndividualQuery } from "@utils/interface/index.interface";
import { Spinner } from "@components/ui/spinner";
import { Button } from "@components/ui/button";

interface RedirectFormProps {
  refetch?: () => void;
}

export const redirectSchema = z
  .object({
    source: z.string().optional(),
    destination: z.string().optional(),
    status_code: z
      .string()
      .refine((value) => ["301", "302", "307", "308", "410"].includes(value), {
        message:
          "Status code must be one of the following: 301, 302, 307, 308, 410",
      }),
    regex: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.status_code === "410") return true;
      return data.destination && data.destination.length > 0;
    },
    {
      message: "Destination URL is required!",
      path: ["destination"],
    }
  )
  .refine(
    (data) =>
      (data.source && data.source.length > 0) ||
      (data.regex && data.regex.length > 0),
    {
      message: "Either Source URL or Regex Pattern is required!",
      path: ["source"],
    }
  )
  .refine(
    (data) =>
      !(
        data.source &&
        data.source.length > 0 &&
        data.regex &&
        data.regex.length > 0
      ),
    {
      message: "Only one of Source URL or Regex Pattern is allowed, not both!",
      path: ["regex"],
    }
  );

const RedirectForm = ({ refetch }: RedirectFormProps) => {
  const { searchParams, handleAction } = useHandleAction({});
  const pageItem = searchParams.get("modal-item");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<RedirectFormData>({
    resolver: zodResolver(redirectSchema),
    defaultValues: {
      source: "",
      destination: "",
      status_code: "301",
      regex: "",
    },
  });

  const statusCode = watch("status_code");

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<RedirectData>
  >({
    endpoint: endpoint.redirect,
    id: pageItem || "",
    key: "redirect",
    enabled: !!pageItem,
  });

  const { mutate, isPending } = usePostData();

  const onSubmit = (data: RedirectFormData) => {
    const payload = {
      ...data,
      destination: data.status_code === "410" ? "" : data.destination || "",
      status_code: parseInt(data.status_code),
      id: pageItem,
    };

    mutate(
      {
        endpoint: endpoint.redirect,
        method: pageItem ? "PATCH" : "POST",
        data: payload,
      },
      {
        onSuccess: () => {
          refetch && refetch();
          handleAction({ type: "modal-close" });
          showToast({
            title: "Success",
            description: `Redirect ${pageItem ? "updated" : "created"} successfully`,
            type: "success",
          });
        },
      }
    );
  };

  useEffect(() => {
    if (fetchData?.data && !fetchPending) {
      reset({
        source: fetchData.data.source || "",
        destination: fetchData.data.destination || "",
        status_code: fetchData.data.status_code.toString(),
        regex: fetchData.data.regex || "",
      });
    } else if (!pageItem) {
      reset({
        source: "",
        destination: "",
        status_code: "301",
        regex: "",
      });
    }
  }, [fetchData, fetchPending, pageItem, reset]);

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "source",
        label: "Source URL",
        type: "text",
        placeholder: "e.g. /old-blog-post",
        colSpan: 12,
        control,
        errors,
      },
      {
        name: "destination",
        label: "Destination URL",
        type: "text",
        placeholder: "e.g. /new-blog-post",
        colSpan: 12,
        control,
        errors,
        visable: statusCode !== "410",
      },
      {
        name: "status_code",
        label: "Status Code",
        type: "select",
        placeholder: "Select Status Code",
        colSpan: 12,
        control,
        errors,
        option: [
          { label: "301 (Permanent Redirect)", value: "301" },
          { label: "302 (Found / Temporary Redirect)", value: "302" },
          { label: "307 (Temporary Redirect)", value: "307" },
          { label: "308 (Permanent Redirect)", value: "308" },
          { label: "410 (Gone)", value: "410" },
        ],
      },
      {
        name: "regex",
        label: "Regex Pattern",
        type: "text",
        placeholder: "e.g. ^/blog/(.*)",
        colSpan: 12,
        control,
        errors,
      },
    ],
    [control, errors, statusCode]
  );

  if (fetchPending && pageItem) {
    return <div className="flex justify-center p-10"><Spinner /></div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="row">
      {formFields.map(
        (field, index) =>
          field.visable !== false && (
            <div key={index} className={`col-md-${field.colSpan} mb-3`}>
              {getInputField(field)}
            </div>
          )
      )}
      <div className="col-12 flex justify-end gap-3 mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleAction({ type: "modal-close" })}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending
            ? "Processing..."
            : pageItem
              ? "Update Redirect"
              : "Create Redirect"}
        </Button>
      </div>
    </form>
  );
};

export default RedirectForm;
